import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import {
  AutocompleteInput,
  ReferenceInput,
  required,
  TextInput,
} from "react-admin";
import CustomDatePickerInput from "../../../components/CustomDatePickerInput";
import moment from "moment";
import SubcontractorAdhocCategoryInput from "../../subcontractorAdhocFees/components/SubcontractorAdhocCategoryInput";
import CustomNumberInput from "../../../components/CustomNumberInput";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { useForm, useFormState } from "react-final-form";
import CustomMonthPickerInput from "../../../components/CustomMonthPickerInput";
import { fetchAllData } from "../../../dataProvider";

interface ISubcontractorTimesheetAdhocFeeInput {
  fieldPrefix: "old" | "new";
}

const SubcontractorTimesheetAdhocFeeInput = ({fieldPrefix}: ISubcontractorTimesheetAdhocFeeInput) => {
  const form = useForm();
  // Only subscribe to values to avoid unnecessary re-renders
  const { values } = useFormState({ subscription: { values: true } });

  const [excludedDates, setExcludedDates] = useState<Date[]>([]);

  // Fetch all the non pending subcontractor purchase invoice summaries
  // than populate the excludedDates based on non pending subcontractor purchase invoice summaries
  useEffect(() => {
    fetchAllData("subcontractorPurchaseInvoiceSummaries", {
      StatusIn: [
        "GeneratingSubcontractorPurchaseInvoice",
        "ReadyToApprove",
        "Approved",
      ],
    })
      .then((res) => {
        const dates = res.map(
          (summary: any) => new Date(summary.year, summary.month - 1, 1)
        );
        setExcludedDates(dates);
      });
  }, []);

  const {
    subcontractorId = "",
    newSubcontractorId = "",
    schoolId = "",
    date = "",
    routeName = "",
  } = values;

  useEffect(() => {
    const selectedSubcontractorId =
      fieldPrefix === "old" ? subcontractorId : newSubcontractorId;

    form.change(`${fieldPrefix}AdhocFeeSubcontractorId`, selectedSubcontractorId);
    form.change(`${fieldPrefix}SchoolId`, schoolId);
    form.change(`${fieldPrefix}Date`, date);
    form.change(`${fieldPrefix}RouteName`, routeName);
  }, [
    fieldPrefix,
    form,
    subcontractorId,
    newSubcontractorId,
    schoolId,
    date,
    routeName,
  ]);

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">General Info</Typography>
      </Grid>

      <Grid item xs={12}>
        <ReferenceInput
          label="Subcontractor"
          source={`${fieldPrefix}AdhocFeeSubcontractorId`}
          reference="subcontractors"
          validate={required()}
          disabled
        >
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
      </Grid>

      <Grid item xs={12}>
        <ReferenceInput
          label="School"
          source={`${fieldPrefix}SchoolId`}
          reference="schools"
          validate={required()}
          filterToQuery={(searchText: any) => {
            if (searchText !== "") return { search: searchText };
          }}
          disabled
        >
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Adhoc Fee Detail</Typography>
      </Grid>

      <Grid item xs={12}>
        <CustomDatePickerInput
          name={`${fieldPrefix}Date`}
          label="Date"
          max={moment().format("Y-MM-DD")}
          disableValue
        />
      </Grid>

      <Grid item xs={12}>
        <CustomMonthPickerInput
          name={`${fieldPrefix}PlanningIssuedAdhocFee`}
          label="Planning Issued Adhoc Fee"
          monthFieldName={`${fieldPrefix}PlanningIssuedMonth`}
          yearFieldName={`${fieldPrefix}PlanningIssuedYear`}
          excludeDates={excludedDates}
          isRequired
        />
      </Grid>

      <Grid item xs={12}>
        <TextInput
          source={`${fieldPrefix}RouteName`}
          label="Route Name (Free text)"
          validate={required()}
          disabled
        />
      </Grid>

      <Grid item xs={12}>
        <SubcontractorAdhocCategoryInput
          source={`${fieldPrefix}Category`}
          label="Category"
        />
      </Grid>

      <Grid item xs={12}>
        <CustomNumberInput
          source={`${fieldPrefix}Price`}
          label="Amount ($)"
          isRequired
        />
      </Grid>

      <Grid item xs={12}>
        <TextInput
          multiline={true}
          label="Remarks"
          source={`${fieldPrefix}Remarks`}
          inputProps={{ maxLength: 90 }}
          validate={required()}
        />
      </Grid>
    </SanitizedGrid>
  );
}

export default SubcontractorTimesheetAdhocFeeInput;